import { FC, useEffect, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import styles from './AddOnsGrid.module.css';
import { ApplicableAddonProps } from '../types';
import { reorderAddonsPut } from '../services';
import { PunchupDraggableTile } from 'features/common/components/Punchup/composites/tiles/PunchupDraggableTile';
import { PunchupPictureTile } from 'features/common/components/Punchup/composites/tiles/PunchupPictureTile';
import { formatMoney } from 'utils/utils';
import { postToast } from 'features/common/utils/postFlash';

interface AddOnsGridProps {
	addons: ApplicableAddonProps[];
	/** The React Query key the add-ons list is cached under — used for the optimistic reorder write. */
	queryKey: QueryKey;
}

const sortByOrderIndex = (items: ApplicableAddonProps[]): ApplicableAddonProps[] =>
	[...items].sort((a, b) =>
		a.addon.orderIndex !== b.addon.orderIndex ? a.addon.orderIndex - b.addon.orderIndex : a.addon.iD - b.addon.iD
	);

/**
 * Reorder the cached add-ons to match the new on-screen order and re-stamp `orderIndex`, so the
 * `sortByOrderIndex` re-sort preserves the new order after the optimistic cache write. Keyed by `iD`.
 * Falls back to the untouched cache on any id mismatch rather than dropping rows.
 * (Mirrors `reorderCachedTicketTypes` — see TicketTypesSection/CLAUDE.md.)
 */
const reorderCachedAddons = (
	cached: ApplicableAddonProps[] | undefined,
	newOrder: ApplicableAddonProps[]
): ApplicableAddonProps[] | undefined => {
	if (!cached) return cached;
	const byId = new Map(cached.map((item) => [item.addon.iD, item]));
	const reordered = newOrder
		.map((item, idx) => {
			const cachedItem = byId.get(item.addon.iD);
			return cachedItem ? { ...cachedItem, addon: { ...cachedItem.addon, orderIndex: idx } } : undefined;
		})
		.filter((item): item is ApplicableAddonProps => item !== undefined);
	return reordered.length === cached.length ? reordered : cached;
};

/**
 * Drag-to-reorder list of add-on tiles. The order is auto-saved on drop (no Save button): the local list
 * and the React Query cache move in lock-step (flicker-free), then `PUT /addons/reorder` persists. A
 * success/error toast fires in the parent frame; on failure both the list and cache roll back.
 */
const AddOnsGrid: FC<AddOnsGridProps> = ({ addons, queryKey }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [ordered, setOrdered] = useState<ApplicableAddonProps[]>(() => sortByOrderIndex(addons));

	// Seed from the prop and re-sync on real data changes. `ordered` is NOT a naked prop mirror — it is also
	// written imperatively by `handleDragEnd` (optimistic drop + rollback), so the order must live in local
	// state. `addons` is a stable react-query reference (the hook uses no `select`), so this fires only on a
	// real reload, which the lock-step cache write below reconciles. Mirrors TicketTypeGrid.
	useEffect(() => {
		setOrdered(sortByOrderIndex(addons));
	}, [addons]);

	const handleDragEnd = async (result: DropResult) => {
		if (!result.destination || result.destination.index === result.source.index) return;

		const reordered = [...ordered];
		const [moved] = reordered.splice(result.source.index, 1);
		reordered.splice(result.destination.index, 0, moved);
		const withNewOrder = reordered.map((item, idx) => ({
			...item,
			addon: { ...item.addon, orderIndex: idx },
		}));

		// Optimistic: move the local list (smooth drop) AND the React Query cache in lock-step, so no
		// refetch can snap rows back to the old order while the PUT is in flight. We deliberately DON'T
		// invalidate on success — the cache already holds the persisted order. Roll both back on error.
		const previous = ordered;
		const previousCache = queryClient.getQueryData<ApplicableAddonProps[]>(queryKey);
		setOrdered(withNewOrder);
		queryClient.setQueryData<ApplicableAddonProps[]>(queryKey, (cached) => reorderCachedAddons(cached, withNewOrder));

		try {
			await reorderAddonsPut(withNewOrder.map(({ addon }) => ({ iD: addon.iD, orderIndex: addon.orderIndex })));
			postToast('success', t('dashboard-addOns-reorder-success', 'Add-ons re-ordered'));
		} catch {
			setOrdered(previous);
			queryClient.setQueryData(queryKey, previousCache);
			postToast('error', t('dashboard-addOns-reorder-error', 'Failed to re-order add-ons'));
		}
	};

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="addons">
				{(provided) => (
					<div
						ref={provided.innerRef}
						{...provided.droppableProps}
						className="punchupDraggableList"
						data-cy="addons-list"
					>
						{ordered.map(({ addon }, i) => (
							<PunchupDraggableTile key={addon.iD} id={String(addon.iD)} index={i}>
								<PunchupPictureTile
									name={addon.name}
									imageUrl={addon.image || undefined}
									imageAlt={addon.name}
									descriptions={[`$${formatMoney(addon.defaultPrice)}`]}
									topRight={<span className={styles.position}>{i + 1}</span>}
									dataCy={`addon-tile-${addon.iD}`}
								/>
							</PunchupDraggableTile>
						))}
						{provided.placeholder}
					</div>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export { AddOnsGrid };
